import {
  CustomViewAttribute,
  CustomViewFilterQueryParams,
  CustomViewFormConditionField,
  CustomViewFormConditionItem,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { filterValidCustomViewFilters } from './rule-validity';

export type FieldAccessChecker = (
  field: Pick<CustomViewFormConditionField, 'allowedRules'>,
) => boolean;

/**
 * Builds a checker that grants access to fields without `allowedRules`
 * and to fields whose rules the user holds (any of).
 */
export const createFieldAccessChecker =
  (
    checkValidity: (
      rules: NonNullable<CustomViewFormConditionField['allowedRules']>,
    ) => boolean,
  ): FieldAccessChecker =>
  (field) =>
    !field.allowedRules?.length || checkValidity(field.allowedRules);

/**
 * Strips filter fields the user cannot access from a custom view entity config.
 */
export function filterRestrictedFilterAttributes<
  T extends {
    filterAttributesList: CustomViewFormConditionField[];
    filterAttributesMap: Map<string, CustomViewFormConditionField>;
  },
>(config: T, hasFieldAccess: FieldAccessChecker): T {
  const filterAttributesList = config.filterAttributesList.filter(hasFieldAccess);

  if (filterAttributesList.length === config.filterAttributesList.length) {
    return config;
  }

  return {
    ...config,
    filterAttributesList,
    filterAttributesMap: new Map(
      [...config.filterAttributesMap].filter(([, field]) => hasFieldAccess(field)),
    ),
  };
}

/**
 * Strips display attributes (columns) the user cannot access from a custom view entity config.
 */
export function filterRestrictedDisplayAttributes<
  T extends {
    attributesList: CustomViewAttribute[];
    attributesMap: Map<string, CustomViewAttribute>;
  },
>(config: T, hasFieldAccess: FieldAccessChecker): T {
  const attributesList = config.attributesList.filter(hasFieldAccess);

  if (attributesList.length === config.attributesList.length) {
    return config;
  }

  return {
    ...config,
    attributesList,
    attributesMap: new Map(
      [...config.attributesMap].filter(([, attribute]) => hasFieldAccess(attribute)),
    ),
  };
}

/**
 * Merges query params contributed by the fields of valid rules the user has access to.
 */
export function resolveFilterQueryParams(
  rules: CustomViewFormConditionItem[],
  hasFieldAccess: FieldAccessChecker,
): CustomViewFilterQueryParams {
  return filterValidCustomViewFilters(rules).reduce<CustomViewFilterQueryParams>(
    (params, rule) =>
      rule.field?.queryParams && hasFieldAccess(rule.field)
        ? { ...params, ...rule.field.queryParams }
        : params,
    {},
  );
}
